import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import InstallPwaPrompt from "./components/InstallPwaPrompt";
import { ConfirmProvider } from "./components/ConfirmProvider";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { CallBubbleProvider } from "./contexts/CallBubbleContext";
import { PhoneProvider } from "./contexts/PhoneContext";
import NotasProvider from "./components/notas/NotasProvider";
import TelegramProvider from "./components/telegram/TelegramProvider";
import { ErrorBoundary } from "./components/ErrorBoundary";
import Layout from "./components/Layout";
import AdminCentralLayout from "./components/AdminCentralLayout";
// Kept static (not lazy) — needed for the very first paint, before auth
// resolves: the public landing page and every auth flow (login, signup,
// password recovery). Everything below them is a private/founder/Beauty
// page only reached AFTER navigation, so it's safe to code-split.
import LoginPage from "./pages/LoginPage";
import LandingPage from "./pages/LandingPage";
import SignupPage from "./pages/SignupPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DeviceGate from "./components/DeviceGate";
import { Toaster } from "./components/ui/sonner";
import { CircleNotch } from "@phosphor-icons/react";
import LoadingScreen from "./components/LoadingScreen";
import { safeOnboardingNext } from "./lib/onboardingNext";

// Lazy-loaded top-level pages (Bloque 4 — code splitting). Each becomes its
// own webpack chunk, fetched only when its route is actually visited.
const SuperAdminPage = lazy(() => import("./pages/SuperAdminPage"));
// FASE 1 — Admin Central shell (namespace /admin-central/*).
const AdminCentralDashboardPage = lazy(() => import("./pages/AdminCentralDashboardPage"));
const AdminCentralBillingPage = lazy(() => import("./pages/AdminCentralBillingPlaceholderPage"));
const AdminCentralProspectsPage = lazy(() => import("./pages/AdminCentralProspectsPage"));
// Commandix Labels — módulo nuevo e independiente dentro de Admin Central
// (Ticket Maestro, 2026-09-26): venta/gestión de etiquetas Shippo para
// clientes propios del Founder. No pertenece a Call Center ni Ruta Comercial.
const AdminCentralLabelsPage = lazy(() => import("./pages/AdminCentralLabelsPage"));
const FounderChooserPage = lazy(() => import("./pages/FounderChooserPage"));
const BackupsAdminPage = lazy(() => import("./pages/BackupsAdminPage"));
const AdminMaintenancePage = lazy(() => import("./pages/AdminMaintenancePage"));
const AdminSecurityPage = lazy(() => import("./pages/AdminSecurityPage"));
const MigrationAdminPage = lazy(() => import("./pages/MigrationAdminPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const ClientesPage = lazy(() => import("./pages/ClientesPage"));
const ClienteDetailPage = lazy(() => import("./pages/ClienteDetailPage"));
const CorreosPage = lazy(() => import("./pages/CorreosPage"));
const EnviarPage = lazy(() => import("./pages/EnviarPage"));
const EmpleadosPage = lazy(() => import("./pages/EmpleadosPage"));
const AjustesPage = lazy(() => import("./pages/AjustesPage"));
const TrackingPage = lazy(() => import("./pages/TrackingPage"));
const FollowUpPage = lazy(() => import("./pages/FollowUpPage"));
const EtiquetasReutilizablesPage = lazy(() => import("./pages/EtiquetasReutilizablesPage"));
const SolicitudesEtiquetaPage = lazy(() => import("./pages/SolicitudesEtiquetaPage"));
const DispositivosAutorizadosPage = lazy(() => import("./pages/DispositivosAutorizadosPage"));
const NotificacionesPage = lazy(() => import("./pages/NotificacionesPage"));
const HistorialCorreosPage = lazy(() => import("./pages/HistorialCorreosPage"));
const MetricasPage = lazy(() => import("./pages/MetricasPage"));
const GastosPage = lazy(() => import("./pages/GastosPage"));
const FinanzasPage = lazy(() => import("./pages/FinanzasPage"));
const AuditoriaPage = lazy(() => import("./pages/AuditoriaPage"));
const OrdersPage = lazy(() => import("./pages/OrdersPage"));
const HistorialOrdenesPage = lazy(() => import("./pages/HistorialOrdenesPage"));
const EquiposPage = lazy(() => import("./pages/EquiposPage"));
const ChatPage = lazy(() => import("./pages/ChatPage"));
const DispositivosPage = lazy(() => import("./pages/DispositivosPage"));
const MisPagosPage = lazy(() => import("./pages/MisPagosPage"));
const MisRetornosPage = lazy(() => import("./pages/MisRetornosPage"));
const ShippoLabelsPage = lazy(() => import("./pages/ShippoLabelsPage"));
const BillingPage = lazy(() => import("./pages/BillingPage"));
const HanoyBusinessPage = lazy(() => import("./pages/HanoyBusinessPage"));
const HanoyOwnerPortal = lazy(() => import("./pages/HanoyOwnerPortal"));
const HanoyProfessionalPortal = lazy(() => import("./pages/HanoyProfessionalPortal"));
const ForcePasswordChangePage = lazy(() => import("./pages/ForcePasswordChangePage"));
const BeautySuitePublicPage = lazy(() => import("./pages/BeautySuitePublicPage"));
const BeautyBookingPage = lazy(() => import("./pages/BeautyBookingPage"));
const BeautyAppointmentManagePage = lazy(() => import("./pages/BeautyAppointmentManagePage"));
const BeautyCoursesPublicPage = lazy(() => import("./pages/BeautyCoursesPublicPage"));
const BeautyOnboardingWizardPage = lazy(() => import("./pages/BeautyOnboardingWizardPage"));
const AgendaPage = lazy(() => import("./pages/AgendaPage"));
const RecordatoriosPage = lazy(() => import("./pages/RecordatoriosPage"));
const CatalogoPage = lazy(() => import("./pages/CatalogoPage"));
const PedidosPage = lazy(() => import("./pages/PedidosPage"));
const InventarioPage = lazy(() => import("./pages/InventarioPage"));
const CentroLlamadasPage = lazy(() => import("./pages/CentroLlamadasPage"));
const PantallaLlamadasPage = lazy(() => import("./pages/PantallaLlamadasPage"));
const NotasPage = lazy(() => import("./pages/NotasPage"));
const NotasAparte = lazy(() => import("./pages/NotasAparte"));
const ChatAparte = lazy(() => import("./pages/ChatAparte"));

const FOUNDER_EMAIL_LOWER = "consulting@vzwhelpscenter.com";

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) return <Navigate to="/login" replace />;

  // Forced initial-password change wins over every other redirect — the
  // backend blocks all other endpoints for this account anyway.
  if (user.force_password_change) return <Navigate to="/force-password-change" replace />;

  // Business owners AND the Hanoy secretary cannot enter the Call Center CRM — redirect to the tenant portal.
  if ((user.role === "business_owner" || user.role === "hanoy_secretaria") && user.business_id) {
    return <Navigate to={`/portal/${user.business_id}`} replace />;
  }
  // Hanoy professionals get their own minimal shell — never the owner/secretary portal or the CRM.
  if (user.role === "hanoy_professional" && user.business_id) {
    return <Navigate to={`/pro/${user.business_id}`} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <DeviceGate user={user}>
      <Layout><ErrorBoundary><Suspense fallback={<LoadingScreen />}>{children}</Suspense></ErrorBoundary></Layout>
    </DeviceGate>
  );
}

// Ventana aparte (p. ej. la TV de la sala): mismas reglas de acceso que ProtectedRoute pero SIN menús ni Layout.
function StandaloneRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.force_password_change) return <Navigate to="/force-password-change" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return <DeviceGate user={user}><ErrorBoundary><Suspense fallback={<LoadingScreen />}>{children}</Suspense></ErrorBoundary></DeviceGate>;
}

// Business-owner-only portal route (no Layout because it has its own UI).
function BusinessOwnerRoute({ children, businessId }) {
  const { user, loading } = useAuth();
  if (loading) {
    return <LoadingScreen />;
  }
  if (!user) return <Navigate to="/login" replace />;
  // Allow founder (impersonate) OR the matching business owner
  const isFounder = (user.email || "").toLowerCase() === FOUNDER_EMAIL_LOWER;
  const owns = user.role === "business_owner" && user.business_id === businessId;
  if (!isFounder && !owns) {
    return <Navigate to="/dashboard" replace />;
  }
  return <ErrorBoundary>{children}</ErrorBoundary>;
}

// Hanoy owner portal route (/portal/:businessId). Shared by business_owner AND
// hanoy_secretaria. business_owner is exempt from the device gate server-side
// (get_current_user), so it keeps the original no-gate behavior here to avoid
// any regression. hanoy_secretaria is NOT exempt server-side, so it must go
// through the same DeviceGate used by every other protected route — otherwise
// its first API call after login hits a permanent 403 DEVICE_REQUIRED.
function HanoyOwnerPortalRoute() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  const isFounder = (user.email || "").toLowerCase() === FOUNDER_EMAIL_LOWER;
  const content = (
    <ErrorBoundary><Suspense fallback={<LoadingScreen />}><HanoyOwnerPortal /></Suspense></ErrorBoundary>
  );
  if (isFounder || user.role === "business_owner") {
    return content;
  }
  return <DeviceGate user={user}>{content}</DeviceGate>;
}

// Hanoy professional portal route (/pro/:businessId). hanoy_professional is
// not exempt from the device gate server-side, so it needs the same DeviceGate
// wrapping as every other protected route.
function HanoyProfessionalPortalRoute() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  const content = (
    <ErrorBoundary><Suspense fallback={<LoadingScreen />}><HanoyProfessionalPortal /></Suspense></ErrorBoundary>
  );
  return <DeviceGate user={user}>{content}</DeviceGate>;
}

// Founder-only route: ONLY the literal founder email can access (not other admins).
// Optional `fallbackRoles` lets specific roles in (e.g. bilingue using /metricas as
// "Seguimiento Retornos"). Optional `allowExtendedManager` lets a gerente whose
// team has `extended_manager=true` access founder-only modules (Shippo, Reusable,
// Empleados, Auditoría, Ajustes) — backend still enforces team_id isolation.
// iter157 Fase 1.5d: `allowAttGerente` gives an AT&T gerente (role=gerente,
// company="att") access to Ajustes (scoped view) and Empleados (his own team
// only). ATT gerentes do NOT get extended_manager so Resend config stays hidden.
function ProtectedFounderRoute({ children, fallbackRoles = [], allowExtendedManager = false, allowAttGerente = false, blockAttGerente = false }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) return <Navigate to="/login" replace />;

  const isFounder = (user.email || "").toLowerCase() === FOUNDER_EMAIL_LOWER;
  const isFallback = fallbackRoles.includes(user.role);
  const userCompany = (user?.team_features?.company || "verizon").toLowerCase();
  const isAttGerenteUser = user.role === "gerente" && userCompany === "att";
  // iter158 — Some modules must be HARD-blocked for AT&T gerentes even if
  // they carry extended_manager (Jhon does). Shippo/PirateShip label
  // generation is admin-only for AT&T teams — they must use Solicitudes
  // de Etiqueta instead. Founder still bypasses everything.
  if (blockAttGerente && !isFounder && isAttGerenteUser) {
    return <Navigate to="/solicitudes-etiqueta" replace />;
  }
  const isExtended = allowExtendedManager
    && user.role === "gerente"
    && !!user?.team_features?.extended_manager
    // AT&T extended managers cannot use this bypass when blockAttGerente is set.
    && !(blockAttGerente && isAttGerenteUser);
  const isAttGerente = allowAttGerente && isAttGerenteUser;

  if (!isFounder && !isFallback && !isExtended && !isAttGerente) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <DeviceGate user={user}>
      <Layout><ErrorBoundary><Suspense fallback={<LoadingScreen />}>{children}</Suspense></ErrorBoundary></Layout>
    </DeviceGate>
  );
}

// FASE 1 — Admin Central. Founder ÚNICAMENTE (no otros admins, no
// gerente/asistente/bilingue) — a diferencia de ProtectedFounderRoute, NO
// envuelve en el <Layout> de Call Center: AdminCentralLayout se auto-envuelve
// dentro de cada página, así que aquí solo se resuelve auth + guard.
function AdminCentralRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return <LoadingScreen />;
  }
  if (!user) return <Navigate to="/login" replace />;
  const isFounder = (user.email || "").toLowerCase() === FOUNDER_EMAIL_LOWER;
  if (!isFounder) return <Navigate to="/dashboard" replace />;
  return (
    <DeviceGate user={user}>
      <ErrorBoundary><Suspense fallback={<LoadingScreen />}>{children}</Suspense></ErrorBoundary>
    </DeviceGate>
  );
}

export function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return <LoadingScreen />;
  }
  if (user) {
    // N1 (validador único de next, ver lib/onboardingNext.js) -- un dueño
    // (o cualquier usuario) con sesión YA abierta que vuelve a /login con
    // ?next=/onboarding/<token> válido (p. ej. tras abrir el enlace en otra
    // pestaña, o tras recuperar su contraseña) debe volver a ESE enlace,
    // no a su destino por defecto (portal/dashboard/start). Un `next`
    // ausente o inválido (incluida cualquier URL externa/maliciosa) cae en
    // el comportamiento de siempre, sin excepción.
    const next = safeOnboardingNext(new URLSearchParams(window.location.search).get("next"));
    if (next) {
      return <Navigate to={next} replace />;
    }
    // Business owners go directly to their own portal, not the call-center CRM.
    if (user.business_id && user.role === "business_owner") {
      return <Navigate to={`/portal/${user.business_id}`} replace />;
    }
    // Founder con sesión ya activa que revisita /login (o /) también debe
    // caer en el lobby, no directo a Call Center.
    if ((user.email || "").toLowerCase() === FOUNDER_EMAIL_LOWER) {
      return <Navigate to="/start" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function HomeRoute() {
  // CRM access (legacy subdomain `adm.` OR new path `/admin`):
  //   - Logged out → /login
  //   - Logged in  → /dashboard
  // Public landing on commandixcrm.com root.
  // Logged-in users on root are sent to dashboard for convenience.
  const { user, loading } = useAuth();
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const isAdmDomain = /^adm\./i.test(host);

  if (loading) {
    return <LoadingScreen />;
  }
  const isFounderUser = (user?.email || "").toLowerCase() === FOUNDER_EMAIL_LOWER;
  if (isAdmDomain) {
    return <Navigate to={user ? (isFounderUser ? "/start" : "/dashboard") : "/login"} replace />;
  }
  if (user) return <Navigate to={isFounderUser ? "/start" : "/dashboard"} replace />;
  return <LandingPage />;
}

function AdminEntryRoute() {
  // Path-based CRM entry (commandixcrm.com/admin)
  // Mirrors the legacy adm. subdomain behavior.
  const { user, loading } = useAuth();
  if (loading) {
    return <LoadingScreen />;
  }
  return <Navigate to={user ? "/dashboard" : "/login"} replace />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NotificationProvider>
        <CallBubbleProvider>
        <PhoneProvider>
          <ConfirmProvider>
          <NotasProvider>
          <TelegramProvider>
          <Toaster position="top-right" closeButton theme="dark" />
          <InstallPwaPrompt />
          <Routes>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/admin" element={<AdminEntryRoute />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/force-password-change" element={<ErrorBoundary><Suspense fallback={<LoadingScreen />}><ForcePasswordChangePage /></Suspense></ErrorBoundary>} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/super-admin" element={<ProtectedFounderRoute><SuperAdminPage /></ProtectedFounderRoute>} />
          {/* FASE 1 — Admin Central. Founder-only, mismo patrón estricto que /super-admin. */}
          {/* Pantalla de elección post-login del Founder (Admin Central | Call Center). */}
          <Route path="/start" element={<AdminCentralRoute><FounderChooserPage /></AdminCentralRoute>} />
          <Route path="/admin-central" element={<AdminCentralRoute><AdminCentralDashboardPage /></AdminCentralRoute>} />
          <Route path="/admin-central/facturacion" element={<AdminCentralRoute><AdminCentralLayout><AdminCentralBillingPage /></AdminCentralLayout></AdminCentralRoute>} />
          <Route path="/admin-central/equipos" element={<AdminCentralRoute><AdminCentralLayout><EquiposPage /></AdminCentralLayout></AdminCentralRoute>} />
          <Route path="/admin-central/labels" element={<AdminCentralRoute><AdminCentralLayout><AdminCentralLabelsPage /></AdminCentralLayout></AdminCentralRoute>} />
          {/* Ruta Comercial / Clientes — modulo vacio, en planificacion por
              fases (Founder, 2026-09-18: retiro de los intentos previos,
              a la espera del plan completo). */}
          {/* Master-detail de escritorio (Founder, 2026-09-22): :id anidado
              bajo la misma ruta/elemento para que AdminCentralProspectsPage
              NUNCA se desmonte al seleccionar un cliente distinto — evita
              recargar el listado en cada selección. El componente lee el
              :id opcional via useParams(); en <1024px decide internamente
              si muestra listado o ficha completa de página. */}
          <Route path="/admin-central/ruta-comercial" element={<AdminCentralRoute><AdminCentralLayout><AdminCentralProspectsPage /></AdminCentralLayout></AdminCentralRoute>}>
            <Route path=":id" element={null} />
          </Route>
          {/* /admin-central/dev-payment-module retirado del producto (Founder,
              2026-09-16) — PaymentMethodModule.jsx y DevPaymentModulePreview.jsx
              se preservan en disco como trabajo experimental, fuera del commit
              productivo, sin ruta que los sirva. */}
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/clientes" element={<ProtectedRoute><ClientesPage /></ProtectedRoute>} />
          <Route path="/clientes/:id" element={<ProtectedRoute><ClienteDetailPage /></ProtectedRoute>} />
          <Route path="/correos" element={<ProtectedFounderRoute><CorreosPage /></ProtectedFounderRoute>} />
          <Route path="/enviar" element={<ProtectedRoute><EnviarPage /></ProtectedRoute>} />
          <Route path="/historial-correos" element={<ProtectedRoute><HistorialCorreosPage /></ProtectedRoute>} />
          <Route path="/tracking" element={<ProtectedRoute allowedRoles={["admin", "asistente", "bilingue", "gerente"]}><TrackingPage /></ProtectedRoute>} />
          <Route path="/clientes-por-contactar" element={<ProtectedRoute allowedRoles={["admin", "asistente", "bilingue", "gerente"]}><FollowUpPage /></ProtectedRoute>} />
          <Route path="/etiquetas-reutilizables" element={<ProtectedRoute allowedRoles={["admin", "gerente"]}><EtiquetasReutilizablesPage /></ProtectedRoute>} />
          <Route path="/solicitudes-etiqueta" element={<ProtectedRoute allowedRoles={["admin", "asistente", "bilingue", "gerente"]}><SolicitudesEtiquetaPage /></ProtectedRoute>} />
          <Route path="/dispositivos-autorizados" element={<ProtectedRoute allowedRoles={["admin"]}><DispositivosAutorizadosPage /></ProtectedRoute>} />
          <Route path="/seguimiento-entregas" element={<Navigate to="/clientes-por-contactar" replace />} />
          <Route path="/notificaciones" element={<ProtectedRoute><NotificacionesPage /></ProtectedRoute>} />
          <Route path="/ordenes" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
          <Route path="/historial-ordenes" element={<ProtectedRoute allowedRoles={["admin", "asistente", "gerente"]}><HistorialOrdenesPage /></ProtectedRoute>} />
          <Route path="/equipos" element={<ProtectedFounderRoute><EquiposPage /></ProtectedFounderRoute>} />
          <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
          <Route path="/empleados" element={<ProtectedFounderRoute allowExtendedManager allowAttGerente><EmpleadosPage /></ProtectedFounderRoute>} />
          <Route path="/metricas" element={<ProtectedFounderRoute fallbackRoles={["bilingue", "gerente", "asistente"]}><MetricasPage /></ProtectedFounderRoute>} />
          <Route path="/auditoria" element={<ProtectedFounderRoute allowExtendedManager><AuditoriaPage /></ProtectedFounderRoute>} />
          <Route path="/ajustes" element={<ProtectedFounderRoute allowExtendedManager allowAttGerente><AjustesPage /></ProtectedFounderRoute>} />
          <Route path="/dispositivos" element={<ProtectedFounderRoute><DispositivosPage /></ProtectedFounderRoute>} />
          <Route path="/mis-pagos" element={<ProtectedRoute allowedRoles={["bilingue"]}><MisPagosPage /></ProtectedRoute>} />
          <Route path="/gastos" element={<ProtectedRoute allowedRoles={["admin", "gerente"]}><GastosPage /></ProtectedRoute>} />
          <Route path="/finanzas" element={<ProtectedRoute allowedRoles={["admin", "gerente"]}><FinanzasPage /></ProtectedRoute>} />
          <Route path="/mis-retornos" element={<ProtectedRoute allowedRoles={["bilingue"]}><MisRetornosPage /></ProtectedRoute>} />
          <Route path="/etiquetas-shippo" element={<ProtectedFounderRoute allowExtendedManager blockAttGerente><ShippoLabelsPage /></ProtectedFounderRoute>} />
          <Route path="/admin/backups" element={<ProtectedFounderRoute><BackupsAdminPage /></ProtectedFounderRoute>} />
          <Route path="/admin/maintenance" element={<ProtectedFounderRoute><AdminMaintenancePage /></ProtectedFounderRoute>} />
          <Route path="/admin/security" element={<ProtectedFounderRoute><AdminSecurityPage /></ProtectedFounderRoute>} />
          <Route path="/admin/migration" element={<ProtectedFounderRoute><MigrationAdminPage /></ProtectedFounderRoute>} />
          <Route path="/billing" element={<ProtectedRoute allowedRoles={["admin", "gerente"]}><BillingPage /></ProtectedRoute>} />
          <Route path="/super-admin/businesses/:businessId" element={<ProtectedFounderRoute><HanoyBusinessPage /></ProtectedFounderRoute>} />
          <Route path="/portal/:businessId" element={<HanoyOwnerPortalRoute />} />
          <Route path="/pro/:businessId" element={<HanoyProfessionalPortalRoute />} />
          <Route path="/b/:slug" element={<ErrorBoundary><Suspense fallback={<LoadingScreen />}><BeautySuitePublicPage /></Suspense></ErrorBoundary>} />
          <Route path="/b/:slug/reservar" element={<ErrorBoundary><Suspense fallback={<LoadingScreen />}><BeautyBookingPage /></Suspense></ErrorBoundary>} />
          <Route path="/b/:slug/cursos" element={<ErrorBoundary><Suspense fallback={<LoadingScreen />}><BeautyCoursesPublicPage /></Suspense></ErrorBoundary>} />
          <Route path="/b/:slug/cita/:token" element={<ErrorBoundary><Suspense fallback={<LoadingScreen />}><BeautyAppointmentManagePage /></Suspense></ErrorBoundary>} />
          <Route path="/onboarding/:token" element={<ErrorBoundary><Suspense fallback={<LoadingScreen />}><BeautyOnboardingWizardPage /></Suspense></ErrorBoundary>} />
          <Route path="/agenda" element={<ProtectedRoute allowedRoles={["admin", "gerente", "asistente", "bilingue"]}><AgendaPage /></ProtectedRoute>} />
          <Route path="/recordatorios" element={<ProtectedRoute allowedRoles={["admin", "gerente"]}><RecordatoriosPage /></ProtectedRoute>} />
          <Route path="/catalogo" element={<ProtectedRoute allowedRoles={["admin", "gerente", "asistente"]}><CatalogoPage /></ProtectedRoute>} />
          <Route path="/pedidos" element={<ProtectedRoute allowedRoles={["admin", "gerente", "asistente"]}><PedidosPage /></ProtectedRoute>} />
          <Route path="/pantalla-llamadas" element={<StandaloneRoute allowedRoles={["admin", "gerente"]}><PantallaLlamadasPage /></StandaloneRoute>} />
          <Route path="/chat-aparte" element={<StandaloneRoute allowedRoles={["admin", "gerente", "asistente", "bilingue"]}><ChatAparte /></StandaloneRoute>} />
          <Route path="/notas" element={<ProtectedRoute allowedRoles={["admin", "gerente", "asistente", "bilingue"]}><NotasPage /></ProtectedRoute>} />
          <Route path="/notas-aparte" element={<StandaloneRoute allowedRoles={["admin", "gerente", "asistente", "bilingue"]}><NotasAparte /></StandaloneRoute>} />
          <Route path="/centro-llamadas" element={<ProtectedRoute allowedRoles={["admin", "gerente", "asistente", "bilingue"]}><CentroLlamadasPage /></ProtectedRoute>} />
          <Route path="/inventario" element={<ProtectedRoute allowedRoles={["admin", "gerente", "asistente"]}><InventarioPage /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
          </TelegramProvider>
          </NotasProvider>
          </ConfirmProvider>
        </PhoneProvider>
        </CallBubbleProvider>
        </NotificationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
